import React, { useEffect, useState } from 'react';
import PortalV2Layout from './PortalV2Layout';
import { useAuth } from '../contexts/AuthContext';
import { User, Phone, Save, CheckCircle, AlertCircle } from 'lucide-react';

// Format any input to XXX-XXX-XXXX as the user types.
export const formatPhone = (value) => {
  const d = (value || '').replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};

const ClientV2Settings = () => {
  const { authFetch } = useAuth();
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '' });
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch('/api/user/profile');
        if (res.ok) {
          const d = await res.json();
          setForm({ first_name: d.first_name || '', last_name: d.last_name || '', phone: formatPhone(d.phone || '') });
          setCompany(d.company_name || '');
          setEmail(d.email || '');
        } else setErr('Failed to load your profile');
      } catch (e) { setErr(e.message); }
      finally { setLoading(false); }
    })();
    /* eslint-disable-next-line */
  }, []);

  const save = async () => {
    setSaving(true); setMsg(null); setErr(null);
    try {
      const res = await authFetch('/api/user/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) setMsg(d.message || 'Your account has been updated.');
      else setErr(d.detail || 'Could not update your account');
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <PortalV2Layout
      eyebrow="Account"
      title="Settings"
      subtitle="Update your name and the phone number used for payments."
      crumbs={<strong>Settings</strong>}
    >
      {err && <div className="pv2-alert pv2-alert-red" data-testid="pv2-settings-error"><AlertCircle size={14} /><span>{err}</span></div>}
      {msg && <div className="pv2-alert pv2-alert-green" data-testid="pv2-settings-success"><CheckCircle size={14} /><span>{msg}</span></div>}

      <div className="pv2-card" style={{ maxWidth: 560, padding: 24 }}>
        {loading ? (
          <p style={{ color: 'var(--pv2-text-muted)' }}>Loading…</p>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label className="pv2-mono-sm" style={{ display: 'block', marginBottom: 6 }}>First Name</label>
                <input
                  className="pv2-input"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  data-testid="pv2-settings-first-name"
                />
              </div>
              <div>
                <label className="pv2-mono-sm" style={{ display: 'block', marginBottom: 6 }}>Last Name</label>
                <input
                  className="pv2-input"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  data-testid="pv2-settings-last-name"
                />
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <label className="pv2-mono-sm" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <Phone size={12} /> Phone (used for payment texts)
              </label>
              <input
                className="pv2-input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
                placeholder="XXX-XXX-XXXX"
                inputMode="numeric"
                data-testid="pv2-settings-phone"
              />
              <p style={{ fontSize: 12, color: 'var(--pv2-text-subtle)', marginTop: 6 }}>
                This is the number our payment processor texts during checkout. Use a number you can receive texts on.
              </p>
            </div>

            {/* Read-only fields kept in sync with ServeManager — not editable here */}
            <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label className="pv2-mono-sm" style={{ display: 'block', marginBottom: 6 }}>Firm / Company</label>
                <input className="pv2-input" value={company} disabled data-testid="pv2-settings-company" style={{ opacity: 0.6 }} />
              </div>
              <div>
                <label className="pv2-mono-sm" style={{ display: 'block', marginBottom: 6 }}>Email</label>
                <input className="pv2-input" value={email} disabled data-testid="pv2-settings-email" style={{ opacity: 0.6 }} />
              </div>
            </div>
            <p style={{ fontSize: 11, color: 'var(--pv2-text-subtle)', marginTop: 8 }}>
              Company and email are managed by WesTex Legal to stay in sync with our records. Contact us to change them.
            </p>

            <button
              onClick={save}
              disabled={saving}
              className="pv2-btn pv2-btn-primary"
              style={{ marginTop: 20, padding: '12px 20px' }}
              data-testid="pv2-settings-save"
            >
              {saving ? 'Saving…' : (<><Save size={14} strokeWidth={1.6} /> Save Changes</>)}
            </button>
          </>
        )}
      </div>
    </PortalV2Layout>
  );
};

export default ClientV2Settings;
